import React, { useEffect, useMemo, useState } from "react";
import "./CruiseItineraryManagement.css";
import axios from "axios";

const API_BASE = "https://bmtvendorvendorfinal.onrender.com";
const API_ITINERARIES = "/api/cruise-itineraries";
const API_SHIPS = "/api/vendor/cruise/ships";

const getAuthConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("vendorToken")}` },
});

const emptyPort = { name: "", city: "", country: "", code: "" };

const emptyStop = {
  day: "",
  portName: "",
  city: "",
  country: "",
  portCode: "",
  arrivalTime: "",
  departureTime: "",
  stayDuration: "",
  description: "",
  activities: [],
};

const initialForm = {
  shipId: "",
  itineraryName: "",
  itineraryCode: "",
  description: "",
  durationDays: "",
  durationNights: "",
  departurePort: { ...emptyPort },
  arrivalPort: { ...emptyPort },
  stops: [],
  destinations: [],
  activities: [],
  inclusions: [],
  exclusions: [],
  departureDate: "",
  returnDate: "",
  status: "draft",
};

const listFrom = (data, key) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.[key])) return data[key];
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const formatDateForInput = (date) => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

const CruiseItineraryManagement = () => {
  const [itineraries, setItineraries] = useState([]);
  const [ships, setShips] = useState([]);

  const [loading, setLoading] = useState(false);
  const [shipsLoading, setShipsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // true = full page form (add / edit)
  const [showForm, setShowForm] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [shipFilter, setShipFilter] = useState("");

  const [form, setForm] = useState(initialForm);

  const [coverImage, setCoverImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [existingCoverImage, setExistingCoverImage] = useState("");
  const [existingImages, setExistingImages] = useState([]);

  const [destinationInput, setDestinationInput] = useState("");
  const [activityInput, setActivityInput] = useState("");
  const [inclusionInput, setInclusionInput] = useState("");
  const [exclusionInput, setExclusionInput] = useState("");
  const [stopActivityInputs, setStopActivityInputs] = useState({});

  const [message, setMessage] = useState({ type: "", text: "" });

  const showError = (text) => {
    setMessage({ type: "error", text });
    scrollTop();
  };

  /* =====================================================
     LOAD
  ===================================================== */

  const loadShips = async () => {
    try {
      setShipsLoading(true);
      const res = await axios.get(`${API_BASE}${API_SHIPS}`, getAuthConfig());
      setShips(listFrom(res.data, "ships"));
    } catch (error) {
      console.error("LOAD SHIPS ERROR:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to load cruise ships.",
      });
    } finally {
      setShipsLoading(false);
    }
  };

  const loadItineraries = async () => {
    try {
      setLoading(true);
      const params = {};
      if (shipFilter) params.shipId = shipFilter;
      if (statusFilter) params.status = statusFilter;

      const res = await axios.get(`${API_BASE}${API_ITINERARIES}`, {
        ...getAuthConfig(),
        params,
      });
      setItineraries(listFrom(res.data, "itineraries"));
    } catch (error) {
      console.error("LOAD ITINERARIES ERROR:", error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message || "Failed to load cruise itineraries.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShips();
  }, []);

  useEffect(() => {
    loadItineraries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipFilter, statusFilter]);

  // form khulte / band hote waqt page ke top par jao
  useEffect(() => {
    window.scrollTo({ top: 0 });
    document.querySelector(".cruise-itinerary-page")?.scrollIntoView({ block: "start" });
  }, [showForm]);

  /* =====================================================
     FILTER + STATS
  ===================================================== */

  const filteredItineraries = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return itineraries;

    return itineraries.filter((item) => {
      const shipName = item.shipId?.shipName || item.shipName || "";
      return [
        item.itineraryName,
        item.itineraryCode,
        shipName,
        item.departurePort?.name,
        item.arrivalPort?.name,
      ]
        .filter(Boolean)
        .some((text) => text.toLowerCase().includes(value));
    });
  }, [itineraries, search]);

  const stats = useMemo(
    () => ({
      total: itineraries.length,
      active: itineraries.filter((i) => i.status === "active").length,
      draft: itineraries.filter((i) => i.status === "draft").length,
      inactive: itineraries.filter((i) => i.status === "inactive").length,
    }),
    [itineraries]
  );

  const getShipName = (item) =>
    item.shipId?.shipName ||
    ships.find((s) => s._id === (item.shipId?._id || item.shipId))?.shipName ||
    "Unknown Ship";

  /* =====================================================
     FORM HELPERS
  ===================================================== */

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const updatePort = (portType, field, value) =>
    setForm((prev) => ({
      ...prev,
      [portType]: { ...prev[portType], [field]: value },
    }));

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setCoverImage(null);
    setGalleryImages([]);
    setExistingCoverImage("");
    setExistingImages([]);
    setDestinationInput("");
    setActivityInput("");
    setInclusionInput("");
    setExclusionInput("");
    setStopActivityInputs({});
  };

  const openAddForm = () => {
    resetForm();
    setMessage({ type: "", text: "" });
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    resetForm();
    setMessage({ type: "", text: "" });
  };

  const addArrayItem = (field, value, setter) => {
    const clean = value.trim();
    if (!clean) return;
    setForm((prev) => ({ ...prev, [field]: [...prev[field], clean] }));
    setter("");
  };

  const removeArrayItem = (field, index) =>
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));

  /* =====================================================
     STOPS
  ===================================================== */

  const addStop = () =>
    setForm((prev) => ({
      ...prev,
      stops: [...prev.stops, { ...emptyStop, day: prev.stops.length + 1 }],
    }));

  const removeStop = (index) => {
    setForm((prev) => ({
      ...prev,
      stops: prev.stops
        .filter((_, i) => i !== index)
        .map((stop, i) => ({ ...stop, day: i + 1 })),
    }));
    setStopActivityInputs({});
  };

  const updateStop = (index, field, value) =>
    setForm((prev) => ({
      ...prev,
      stops: prev.stops.map((stop, i) =>
        i === index ? { ...stop, [field]: value } : stop
      ),
    }));

  const addStopActivity = (index) => {
    const value = (stopActivityInputs[index] || "").trim();
    if (!value) return;

    setForm((prev) => ({
      ...prev,
      stops: prev.stops.map((stop, i) =>
        i === index
          ? { ...stop, activities: [...(stop.activities || []), value] }
          : stop
      ),
    }));
    setStopActivityInputs((prev) => ({ ...prev, [index]: "" }));
  };

  const removeStopActivity = (stopIndex, activityIndex) =>
    setForm((prev) => ({
      ...prev,
      stops: prev.stops.map((stop, i) =>
        i === stopIndex
          ? {
              ...stop,
              activities: (stop.activities || []).filter(
                (_, idx) => idx !== activityIndex
              ),
            }
          : stop
      ),
    }));

  /* =====================================================
     EDIT
  ===================================================== */

  const openEditForm = async (item) => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });

      const res = await axios.get(
        `${API_BASE}${API_ITINERARIES}/${item._id}`,
        getAuthConfig()
      );
      const it = res.data?.itinerary || res.data?.data || item;

      setEditingId(it._id);
      setForm({
        shipId: it.shipId?._id || it.shipId || "",
        itineraryName: it.itineraryName || "",
        itineraryCode: it.itineraryCode || "",
        description: it.description || "",
        durationDays: it.durationDays ?? "",
        durationNights: it.durationNights ?? "",
        departurePort: {
          name: it.departurePort?.name || "",
          city: it.departurePort?.city || "",
          country: it.departurePort?.country || "",
          code: it.departurePort?.code || "",
        },
        arrivalPort: {
          name: it.arrivalPort?.name || "",
          city: it.arrivalPort?.city || "",
          country: it.arrivalPort?.country || "",
          code: it.arrivalPort?.code || "",
        },
        stops: Array.isArray(it.stops)
          ? it.stops.map((s) => ({
              ...emptyStop,
              ...s,
              activities: Array.isArray(s.activities) ? s.activities : [],
            }))
          : [],
        destinations: Array.isArray(it.destinations) ? it.destinations : [],
        activities: Array.isArray(it.activities) ? it.activities : [],
        inclusions: Array.isArray(it.inclusions) ? it.inclusions : [],
        exclusions: Array.isArray(it.exclusions) ? it.exclusions : [],
        departureDate: it.departureDate ? formatDateForInput(it.departureDate) : "",
        returnDate: it.returnDate ? formatDateForInput(it.returnDate) : "",
        status: it.status || "draft",
      });

      setExistingCoverImage(it.coverImage || "");
      setExistingImages(Array.isArray(it.images) ? it.images : []);
      setCoverImage(null);
      setGalleryImages([]);
      setShowForm(true);
    } catch (error) {
      console.error("EDIT ITINERARY ERROR:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to load itinerary.",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     SAVE
  ===================================================== */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.shipId) return showError("Please select a cruise ship.");
    if (!form.itineraryName.trim()) return showError("Itinerary name is required.");
    if (!form.durationDays) return showError("Duration days is required.");
    if (form.durationNights === "" || form.durationNights === null)
      return showError("Duration nights is required.");
    if (!form.departurePort.name.trim())
      return showError("Departure port name is required.");
    if (!form.arrivalPort.name.trim())
      return showError("Arrival port name is required.");

    try {
      setSaving(true);

      const data = new FormData();
      data.append("shipId", form.shipId);
      data.append("itineraryName", form.itineraryName.trim());
      data.append("itineraryCode", form.itineraryCode.trim());
      data.append("description", form.description.trim());
      data.append("durationDays", String(form.durationDays));
      data.append("durationNights", String(form.durationNights));
      data.append("departurePort", JSON.stringify(form.departurePort));
      data.append("arrivalPort", JSON.stringify(form.arrivalPort));
      data.append("stops", JSON.stringify(form.stops));
      data.append("destinations", JSON.stringify(form.destinations));
      data.append("activities", JSON.stringify(form.activities));
      data.append("inclusions", JSON.stringify(form.inclusions));
      data.append("exclusions", JSON.stringify(form.exclusions));
      if (form.departureDate) data.append("departureDate", form.departureDate);
      if (form.returnDate) data.append("returnDate", form.returnDate);
      data.append("status", form.status);
      if (coverImage) data.append("coverImage", coverImage);
      galleryImages.forEach((file) => data.append("images", file));

      const config = {
        ...getAuthConfig(),
        headers: {
          ...getAuthConfig().headers,
          "Content-Type": "multipart/form-data",
        },
      };

      const response = editingId
        ? await axios.put(`${API_BASE}${API_ITINERARIES}/${editingId}`, data, config)
        : await axios.post(`${API_BASE}${API_ITINERARIES}`, data, config);

      setShowForm(false);
      resetForm();
      setMessage({
        type: "success",
        text: response.data?.message || "Itinerary saved successfully.",
      });

      await loadItineraries();
    } catch (error) {
      console.error("SAVE ITINERARY ERROR:", error);
      showError(
        error.response?.data?.message ||
          error.message ||
          "Failed to save itinerary."
      );
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
     DELETE
  ===================================================== */

  const deleteItinerary = async (id) => {
    if (!window.confirm("Are you sure you want to delete this itinerary?")) return;

    try {
      setLoading(true);
      const response = await axios.delete(
        `${API_BASE}${API_ITINERARIES}/${id}`,
        getAuthConfig()
      );
      setMessage({
        type: "success",
        text: response.data?.message || "Itinerary deleted successfully.",
      });
      await loadItineraries();
    } catch (error) {
      console.error("DELETE ITINERARY ERROR:", error);
      setMessage({
        type: "error",
        text: error.response?.data?.message || "Failed to delete itinerary.",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     ALERT
  ===================================================== */

  const alertBox = message.text && (
    <div className={`alert ${message.type === "error" ? "alert-error" : "alert-success"}`}>
      <span>{message.text}</span>
      <button type="button" onClick={() => setMessage({ type: "", text: "" })}>
        ×
      </button>
    </div>
  );

  /* =====================================================
     FULL PAGE FORM (Add / Edit)
  ===================================================== */

  if (showForm) {
    return (
      <div className="cruise-itinerary-page">
        <div className="form-page">
          <div className="form-page-header">
            <button
              type="button"
              className="back-btn"
              onClick={closeForm}
              disabled={saving}
            >
              ← Back to itineraries
            </button>

            <span className="page-eyebrow">CRUISE ITINERARY</span>
            <h1>{editingId ? "Edit Itinerary" : "Create Itinerary"}</h1>
            <p>
              Fill in the details below to {editingId ? "update" : "create"} your
              cruise itinerary.
            </p>
          </div>

          {alertBox}

          <form onSubmit={handleSubmit} className="form-page-card">
            {/* BASIC */}
            <section className="form-section">
              <div className="section-title">
                <span>01</span>
                <div>
                  <h3>Basic Information</h3>
                  <p>Define the itinerary and cruise ship.</p>
                </div>
              </div>

              <div className="form-grid">
                <div className="field full">
                  <label>Cruise Ship *</label>
                  <select
                    value={form.shipId}
                    onChange={(e) => updateField("shipId", e.target.value)}
                    required
                  >
                    <option value="">
                      {shipsLoading ? "Loading ships..." : "Select cruise ship"}
                    </option>
                    {ships.map((ship) => (
                      <option key={ship._id} value={ship._id}>
                        {ship.shipName}
                        {ship.cruiseLineName ? ` — ${ship.cruiseLineName}` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Itinerary Name *</label>
                  <input
                    type="text"
                    value={form.itineraryName}
                    onChange={(e) => updateField("itineraryName", e.target.value)}
                    placeholder="e.g. Mediterranean Explorer"
                    required
                  />
                </div>

                <div className="field">
                  <label>Itinerary Code</label>
                  <input
                    type="text"
                    value={form.itineraryCode}
                    onChange={(e) => updateField("itineraryCode", e.target.value)}
                    placeholder="MED-001"
                  />
                </div>

                <div className="field">
                  <label>Duration Days *</label>
                  <input
                    type="number"
                    min="1"
                    value={form.durationDays}
                    onChange={(e) => updateField("durationDays", e.target.value)}
                    required
                  />
                </div>

                <div className="field">
                  <label>Duration Nights *</label>
                  <input
                    type="number"
                    min="0"
                    value={form.durationNights}
                    onChange={(e) => updateField("durationNights", e.target.value)}
                    required
                  />
                </div>

                <div className="field full">
                  <label>Description</label>
                  <textarea
                    rows="4"
                    value={form.description}
                    onChange={(e) => updateField("description", e.target.value)}
                    placeholder="Describe the cruise itinerary..."
                  />
                </div>
              </div>
            </section>

            {/* PORTS */}
            <section className="form-section">
              <div className="section-title">
                <span>02</span>
                <div>
                  <h3>Ports</h3>
                  <p>Define departure and arrival ports.</p>
                </div>
              </div>

              <div className="port-grid">
                {[
                  { key: "departurePort", title: "DEPARTURE", icon: "🚢", name: "Port of Miami", code: "MIA" },
                  { key: "arrivalPort", title: "ARRIVAL", icon: "⚓", name: "Port of Barcelona", code: "BCN" },
                ].map((p) => (
                  <div className="port-card" key={p.key}>
                    <div className="port-heading">
                      <span>{p.title}</span>
                      <b>{p.icon}</b>
                    </div>

                    <div className="field">
                      <label>Port Name *</label>
                      <input
                        type="text"
                        value={form[p.key].name}
                        onChange={(e) => updatePort(p.key, "name", e.target.value)}
                        placeholder={p.name}
                        required
                      />
                    </div>

                    <div className="two-fields">
                      <div className="field">
                        <label>City</label>
                        <input
                          type="text"
                          value={form[p.key].city}
                          onChange={(e) => updatePort(p.key, "city", e.target.value)}
                        />
                      </div>
                      <div className="field">
                        <label>Country</label>
                        <input
                          type="text"
                          value={form[p.key].country}
                          onChange={(e) => updatePort(p.key, "country", e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="field">
                      <label>Port Code</label>
                      <input
                        type="text"
                        value={form[p.key].code}
                        onChange={(e) => updatePort(p.key, "code", e.target.value)}
                        placeholder={p.code}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* STOPS */}
            <section className="form-section">
              <div className="section-title-row">
                <div className="section-title">
                  <span>03</span>
                  <div>
                    <h3>Daily Route &amp; Stops</h3>
                    <p>Add ports and activities for every cruise day.</p>
                  </div>
                </div>

                <button type="button" className="secondary-btn" onClick={addStop}>
                  + Add Day
                </button>
              </div>

              <div className="stops-list">
                {form.stops.length === 0 ? (
                  <div className="no-stops">
                    <span>🗺️</span>
                    <p>No daily stops added yet.</p>
                    <button type="button" onClick={addStop}>
                      Add first day
                    </button>
                  </div>
                ) : (
                  form.stops.map((stop, index) => (
                    <div className="stop-card" key={index}>
                      <div className="stop-top">
                        <div className="day-badge">DAY {index + 1}</div>
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => removeStop(index)}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="form-grid">
                        <div className="field">
                          <label>Port Name *</label>
                          <input
                            type="text"
                            value={stop.portName}
                            onChange={(e) => updateStop(index, "portName", e.target.value)}
                            required
                          />
                        </div>

                        <div className="field">
                          <label>Port Code</label>
                          <input
                            type="text"
                            value={stop.portCode}
                            onChange={(e) => updateStop(index, "portCode", e.target.value)}
                          />
                        </div>

                        <div className="field">
                          <label>City</label>
                          <input
                            type="text"
                            value={stop.city}
                            onChange={(e) => updateStop(index, "city", e.target.value)}
                          />
                        </div>

                        <div className="field">
                          <label>Country</label>
                          <input
                            type="text"
                            value={stop.country}
                            onChange={(e) => updateStop(index, "country", e.target.value)}
                          />
                        </div>

                        <div className="field">
                          <label>Arrival Time</label>
                          <input
                            type="time"
                            value={stop.arrivalTime}
                            onChange={(e) => updateStop(index, "arrivalTime", e.target.value)}
                          />
                        </div>

                        <div className="field">
                          <label>Departure Time</label>
                          <input
                            type="time"
                            value={stop.departureTime}
                            onChange={(e) => updateStop(index, "departureTime", e.target.value)}
                          />
                        </div>

                        <div className="field">
                          <label>Stay Duration</label>
                          <input
                            type="text"
                            value={stop.stayDuration}
                            onChange={(e) => updateStop(index, "stayDuration", e.target.value)}
                            placeholder="6 hours"
                          />
                        </div>

                        <div className="field full">
                          <label>Stop Description</label>
                          <textarea
                            rows="2"
                            value={stop.description}
                            onChange={(e) => updateStop(index, "description", e.target.value)}
                          />
                        </div>

                        <div className="field full">
                          <label>Activities</label>

                          <div className="inline-input">
                            <input
                              type="text"
                              value={stopActivityInputs[index] || ""}
                              onChange={(e) =>
                                setStopActivityInputs((prev) => ({
                                  ...prev,
                                  [index]: e.target.value,
                                }))
                              }
                              placeholder="e.g. City Tour"
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addStopActivity(index);
                                }
                              }}
                            />
                            <button type="button" onClick={() => addStopActivity(index)}>
                              Add
                            </button>
                          </div>

                          <div className="tag-list">
                            {(stop.activities || []).map((activity, activityIndex) => (
                              <span className="tag" key={activityIndex}>
                                {activity}
                                <button
                                  type="button"
                                  onClick={() => removeStopActivity(index, activityIndex)}
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            {/* DESTINATIONS */}
            <section className="form-section">
              <div className="section-title">
                <span>04</span>
                <div>
                  <h3>Destinations &amp; Activities</h3>
                  <p>Add highlights of the itinerary.</p>
                </div>
              </div>

              <TagInput
                label="Destinations"
                placeholder="e.g. Barcelona"
                value={destinationInput}
                setValue={setDestinationInput}
                items={form.destinations}
                addItem={() => addArrayItem("destinations", destinationInput, setDestinationInput)}
                removeItem={(i) => removeArrayItem("destinations", i)}
              />

              <TagInput
                label="Activities"
                placeholder="e.g. Snorkeling"
                value={activityInput}
                setValue={setActivityInput}
                items={form.activities}
                addItem={() => addArrayItem("activities", activityInput, setActivityInput)}
                removeItem={(i) => removeArrayItem("activities", i)}
              />
            </section>

            {/* INCLUSIONS */}
            <section className="form-section">
              <div className="section-title">
                <span>05</span>
                <div>
                  <h3>Inclusions &amp; Exclusions</h3>
                  <p>Clearly define what is included in the cruise.</p>
                </div>
              </div>

              <TagInput
                label="What's Included"
                placeholder="e.g. Breakfast"
                value={inclusionInput}
                setValue={setInclusionInput}
                items={form.inclusions}
                addItem={() => addArrayItem("inclusions", inclusionInput, setInclusionInput)}
                removeItem={(i) => removeArrayItem("inclusions", i)}
                positive
              />

              <TagInput
                label="What's Excluded"
                placeholder="e.g. Port taxes"
                value={exclusionInput}
                setValue={setExclusionInput}
                items={form.exclusions}
                addItem={() => addArrayItem("exclusions", exclusionInput, setExclusionInput)}
                removeItem={(i) => removeArrayItem("exclusions", i)}
                negative
              />
            </section>

            {/* SCHEDULE */}
            <section className="form-section">
              <div className="section-title">
                <span>06</span>
                <div>
                  <h3>Schedule &amp; Status</h3>
                  <p>Set sailing dates and publication status.</p>
                </div>
              </div>

              <div className="form-grid three">
                <div className="field">
                  <label>Departure Date</label>
                  <input
                    type="date"
                    value={form.departureDate}
                    onChange={(e) => updateField("departureDate", e.target.value)}
                  />
                </div>

                <div className="field">
                  <label>Return Date</label>
                  <input
                    type="date"
                    value={form.returnDate}
                    onChange={(e) => updateField("returnDate", e.target.value)}
                  />
                </div>

                <div className="field">
                  <label>Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => updateField("status", e.target.value)}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </section>

            {/* MEDIA */}
            <section className="form-section">
              <div className="section-title">
                <span>07</span>
                <div>
                  <h3>Media</h3>
                  <p>Upload itinerary cover and gallery images.</p>
                </div>
              </div>

              <div className="media-grid">
                <div className="upload-card">
                  <label>Cover Image</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => setCoverImage(e.target.files?.[0] || null)}
                  />

                  {coverImage ? (
                    <div className="selected-file">
                      <span>✓</span> {coverImage.name}
                    </div>
                  ) : existingCoverImage ? (
                    <img
                      className="existing-cover"
                      src={existingCoverImage}
                      alt="Existing cover"
                    />
                  ) : null}
                </div>

                <div className="upload-card">
                  <label>Gallery Images</label>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => setGalleryImages(Array.from(e.target.files || []))}
                  />

                  {galleryImages.length > 0 && (
                    <div className="selected-file">
                      <span>✓</span> {galleryImages.length} image
                      {galleryImages.length !== 1 ? "s" : ""} selected
                    </div>
                  )}

                  {editingId && existingImages.length > 0 && (
                    <div className="existing-gallery">
                      {existingImages.map((image, index) => (
                        <img key={index} src={image} alt={`Gallery ${index + 1}`} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* FOOTER */}
            <div className="form-page-footer">
              <button
                type="button"
                className="cancel-btn"
                onClick={closeForm}
                disabled={saving}
              >
                Cancel
              </button>

              <button type="submit" className="primary-btn" disabled={saving}>
                {saving
                  ? "Saving..."
                  : editingId
                  ? "Update Itinerary"
                  : "Create Itinerary"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  /* =====================================================
     LIST PAGE
  ===================================================== */

  const count = filteredItineraries.length;

  return (
    <div className="cruise-itinerary-page">
      <div className="itinerary-header">
        <div>
          <span className="page-eyebrow">CRUISE MANAGEMENT</span>
          <h1>Itinerary Management</h1>
          <p>Create and manage complete cruise itineraries, daily stops and port schedules.</p>
        </div>

        <button className="primary-btn" onClick={openAddForm}>
          <span>+</span>
          Add Itinerary
        </button>
      </div>

      {alertBox}

      {/* STATS */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🗺️</div>
          <div>
            <span>Total Itineraries</span>
            <strong>{stats.total}</strong>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✓</div>
          <div>
            <span>Active</span>
            <strong>{stats.active}</strong>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div>
            <span>Draft</span>
            <strong>{stats.draft}</strong>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">◷</div>
          <div>
            <span>Inactive</span>
            <strong>{stats.inactive}</strong>
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="filter-card">
        <div className="search-box">
          <span>⌕</span>
          <input
            type="text"
            placeholder="Search itinerary, code, ship or port..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select value={shipFilter} onChange={(e) => setShipFilter(e.target.value)}>
          <option value="">All Ships</option>
          {ships.map((ship) => (
            <option key={ship._id} value={ship._id}>
              {ship.shipName}
            </option>
          ))}
        </select>

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="inactive">Inactive</option>
        </select>

        <button className="refresh-btn" onClick={loadItineraries} disabled={loading}>
          ↻ Refresh
        </button>
      </div>

      {/* TABLE */}
      <div className="table-card">
        <div className="table-header">
          <div>
            <h2>Cruise Itineraries</h2>
            <span>
              {count} {count === 1 ? "itinerary" : "itineraries"}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="table-loading">
            <div className="spinner" />
            Loading itineraries...
          </div>
        ) : count === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🗺️</div>
            <h3>No itineraries found</h3>
            <p>Create your first cruise itinerary to get started.</p>
            <button className="primary-btn" onClick={openAddForm}>
              + Add Itinerary
            </button>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>ITINERARY</th>
                  <th>SHIP</th>
                  <th>DURATION</th>
                  <th>ROUTE</th>
                  <th>STOPS</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>

              <tbody>
                {filteredItineraries.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="itinerary-cell">
                        <div className="thumb">
                          {item.coverImage ? (
                            <img src={item.coverImage} alt={item.itineraryName} />
                          ) : (
                            <span>🛳️</span>
                          )}
                        </div>
                        <div>
                          <strong>{item.itineraryName}</strong>
                          <small>{item.itineraryCode || "No code"}</small>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span className="ship-name">{getShipName(item)}</span>
                    </td>

                    <td>
                      <strong>{item.durationDays}</strong> Days
                      <br />
                      <small>{item.durationNights} Nights</small>
                    </td>

                    <td>
                      <div className="route-cell">
                        <span>{item.departurePort?.name}</span>
                        <b>→</b>
                        <span>{item.arrivalPort?.name}</span>
                      </div>
                    </td>

                    <td>
                      <span className="count-pill">{item.stops?.length || 0}</span>
                    </td>

                    <td>
                      <span className={`status-badge status-${item.status}`}>
                        {item.status}
                      </span>
                    </td>

                    <td>
                      <div className="action-buttons">
                        <button title="View" onClick={() => setViewItem(item)}>
                          👁
                        </button>
                        <button title="Edit" onClick={() => openEditForm(item)}>
                          ✎
                        </button>
                        <button
                          title="Delete"
                          className="delete-action"
                          onClick={() => deleteItinerary(item._id)}
                        >
                          🗑
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* VIEW MODAL (details popup) */}
      {viewItem && (
        <div className="modal-overlay" onMouseDown={() => setViewItem(null)}>
          <div className="view-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="page-eyebrow">ITINERARY DETAILS</span>
                <h2>{viewItem.itineraryName}</h2>
              </div>
              <button className="modal-close" onClick={() => setViewItem(null)}>
                ×
              </button>
            </div>

            {viewItem.coverImage && (
              <img
                className="view-cover"
                src={viewItem.coverImage}
                alt={viewItem.itineraryName}
              />
            )}

            <div className="view-content">
              <div className="view-summary">
                <div>
                  <span>SHIP</span>
                  <strong>{getShipName(viewItem)}</strong>
                </div>
                <div>
                  <span>DURATION</span>
                  <strong>
                    {viewItem.durationDays} Days / {viewItem.durationNights} Nights
                  </strong>
                </div>
                <div>
                  <span>STATUS</span>
                  <strong>{viewItem.status}</strong>
                </div>
              </div>

              {viewItem.description && (
                <div className="view-block">
                  <h3>Description</h3>
                  <p>{viewItem.description}</p>
                </div>
              )}

              <div className="view-route">
                <div>
                  <span>DEPARTURE</span>
                  <strong>{viewItem.departurePort?.name}</strong>
                  <small>
                    {[viewItem.departurePort?.city, viewItem.departurePort?.country]
                      .filter(Boolean)
                      .join(", ")}
                  </small>
                </div>

                <div className="route-arrow">→</div>

                <div>
                  <span>ARRIVAL</span>
                  <strong>{viewItem.arrivalPort?.name}</strong>
                  <small>
                    {[viewItem.arrivalPort?.city, viewItem.arrivalPort?.country]
                      .filter(Boolean)
                      .join(", ")}
                  </small>
                </div>
              </div>

              <div className="view-block">
                <h3>Daily Stops</h3>
                <div className="view-stops">
                  {viewItem.stops?.length ? (
                    viewItem.stops.map((stop, index) => (
                      <div className="view-stop" key={index}>
                        <div className="view-day">{stop.day || index + 1}</div>
                        <div>
                          <strong>{stop.portName}</strong>
                          <small>
                            {[stop.city, stop.country].filter(Boolean).join(", ")}
                          </small>

                          {(stop.activities || []).length > 0 && (
                            <div className="mini-tags">
                              {stop.activities.map((activity, i) => (
                                <span key={i}>{activity}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No stops added.</p>
                  )}
                </div>
              </div>

              <div className="view-two-column">
                <ViewTags title="Destinations" items={viewItem.destinations} />
                <ViewTags title="Activities" items={viewItem.activities} />
                <ViewTags title="Inclusions" items={viewItem.inclusions} />
                <ViewTags title="Exclusions" items={viewItem.exclusions} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* =====================================================
   TAG INPUT
===================================================== */

const TagInput = ({
  label,
  placeholder,
  value,
  setValue,
  items,
  addItem,
  removeItem,
  positive,
  negative,
}) => (
  <div className="tag-input-section">
    <label>{label}</label>

    <div className="inline-input">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addItem();
          }
        }}
      />
      <button type="button" onClick={addItem}>
        Add
      </button>
    </div>

    <div className="tag-list">
      {items.map((item, index) => (
        <span
          className={`tag ${positive ? "tag-positive" : negative ? "tag-negative" : ""}`}
          key={index}
        >
          {positive && "✓ "}
          {negative && "× "}
          {item}
          <button type="button" onClick={() => removeItem(index)}>
            ×
          </button>
        </span>
      ))}
    </div>
  </div>
);

/* =====================================================
   VIEW TAGS
===================================================== */

const ViewTags = ({ title, items }) => {
  if (!items?.length) return null;

  return (
    <div className="view-block">
      <h3>{title}</h3>
      <div className="mini-tags">
        {items.map((item, index) => (
          <span key={index}>{item}</span>
        ))}
      </div>
    </div>
  );
};

export default CruiseItineraryManagement;